<template>
  <BaseBulkMessageListItem
    :is-current="isCurrent"
    v-loading="isBusy"
  >
    <template v-slot:badges>
      <Badge type="email"></Badge>
      <Badge v-if="isDraft" type="draft"></Badge>
      <Badge v-else-if="isQueued" type="queued" :text="badgeQueuedText"></Badge>
      <Badge v-else-if="isScheduled" type="scheduled" :text="badgeSchedulesText"></Badge>
      <Badge v-else-if="isSent" type="sent" :text="badgeSentText"></Badge>
      <Badge v-else-if="isCanceled" type="canceled"></Badge>
    </template>
    <template v-slot:menu>
      <div v-if="isSent || isCanceled" class="dropdown-item" @click="showMessages">Show messages</div>
      <div v-if="isCancelable && canUpdate" @click="cancelSending" class="dropdown-item">Cancel</div>
    </template>
    <template v-slot:header>
      <span class="font-weight-bold">{{ subject }}</span>
    </template>
    <template v-slot:body>
      <div v-html="emailBody"></div>
    </template>
  </BaseBulkMessageListItem>
</template>

<script>
import BaseBulkMessageListItem from "./base-bulk-message-list-item.vue";
import Badge from "./badge.vue";

export default {
  components: {Badge, BaseBulkMessageListItem},
  props: {
    item: null,
    isCurrent: null,
  },

  data() {
    return {
     isBusy: false,
    }
  },

  computed: {
    /**
     * @return {Object|null}
     */
    emailBulkMessage () {
      return _.get(this.item, 'email_bulk_message', null);
    },

    /**
     * @return {string}
     */
    subject() {
      return _.get(this.emailBulkMessage, 'subject', '');
    },

    /**
     * @return {string}
     */
    emailBody() {
      return _.get(this.emailBulkMessage, 'html_body', '');
    },

    /**
     * @return {boolean}
     */
    isDraft() {
      return this.emailBulkMessage.status === 1;
    },

    /**
     * @return {boolean}
     */
    isQueued() {
      return this.emailBulkMessage.status === 2;
    },

    /**
     * @return {boolean}
     */
    isScheduled() {
      return this.emailBulkMessage.status === 3;
    },

    /**
     * @return {boolean}
     */
    isSent() {
      return this.emailBulkMessage.status === 4;
    },

    /**
     * @return {boolean}
     */
    isCanceled() {
      return this.emailBulkMessage.status === 5;
    },

    /**
     * @return {string|null}
     */
    formattedStartAt() {
      const value = _.get(this.item, 'start_at.datetime');

      return value ? this.formatDate(value) : null;
    },

    /**
     * @return {string|null}
     */
    formattedSentAt() {
      const value = _.get(this.emailBulkMessage, 'sent_at.datetime');

      return value ? this.formatDate(value) : null;
    },

    /**
     * @return {string}
     */
    badgeQueuedText() {
      return 'QUEUED' + (this.formattedStartAt ? `: ${this.formattedStartAt}` : '');
    },

    /**
     * @return {string}
     */
    badgeSchedulesText() {
      return 'SCHEDULED' + (this.formattedStartAt ? `: ${this.formattedStartAt}` : '');
    },

    /**
     * @return {string}
     */
    badgeSentText() {
      return 'SENT' + (this.formattedSentAt ? `: ${this.formattedSentAt}` : '');
    },

    /**
     * @return {boolean}
     */
    canUpdate() {
      return _.get(this.emailBulkMessage, 'canUpdate') === true;
    },

    /**
     * @return {boolean}
     */
    isCancelable() {
      return this.emailBulkMessage.isCancelable;
    },
  },

  methods: {
    /**
     * @param date
     * @return {string}
     */
    formatDate(date) {
      return moment(date).format(config('format.moment.short.datetime'));
    },

    /**
     * @emits show-messages
     */
    showMessages() {
      this.$emit('show-messages', {type: 'email-message-list', item: this.item});
    },

    /**
     * @return {Promise}
     */
    cancelSending() {
      this.isBusy = true;
      const url = _.get(this.emailBulkMessage, 'cancelUrl');

      return axios.post(url)
        .then(({data: {message}}) => {
          _.isString(message) && this.$message.success(message);
          this.$emit('refresh-list');
        })
        .catch(this.handleErrorResponse)
        .then(() => this.isBusy = false);
    },

    /**
     * @param data
     * @param status
     * @return {boolean}
     */
    handleErrorResponse({response: {data, status}}) {
      const {errors, message} = data || {};
      if (status === 422 && errors) {
        this.$message.error(message);
      } else if (message && _.isString(message)) {
        this.$message.error(message);
      }
    },
  },
}
</script>
